import type { Answer, CommunityEvent, ContactFact, HelpTopic, NavGroup, NavLink } from "../types/content";
import { clientBrandMedia } from "./clientBrandMedia";

export const SITE_ORIGIN = "https://www.motorcyclelawyermichigan.com";
export const BRAND_NAME = "Jason Waechter- THE Motorcycle Lawyer®";

export const firm = {
  displayName: BRAND_NAME,
  legalName: "Law Offices of Jason A. Waechter",
  attorney: "Jason Waechter",
  mark: "THE Motorcycle Lawyer®",
  phoneDisplay: "877-BIKER-LAW",
  phoneDigits: "877-245-3752",
  phoneHref: "tel:18772453752",
};

export const logos = {
  desktop: {
    src: clientBrandMedia.logo.src,
    width: 96,
    height: 96,
  },
  compact: {
    src: clientBrandMedia.logo.src,
    width: 62,
    height: 62,
  },
};

export const navGroups: NavGroup[] = [
  {
    id: "about",
    label: "About",
    links: [
      { label: "About Our Firm", path: "/about-us/" },
      { label: "Why Hire Us", path: "/why-hire-us/" },
      { label: "Client Testimonials", path: "/law-firm-testimonials/" },
      { label: "As Seen On TV", path: "/biker-lawyer-as-seen-on-tv/" },
      { label: "We Give Back To The MC Community", path: "/helping-the-biker-community/" },
    ],
  },
  {
    id: "attorneys",
    label: "Attorneys",
    links: [
      { label: BRAND_NAME, path: "/team/jason-a-waechter/" },
      { label: "Sean P. Murphy", path: "/team/sean-p-murphy/" },
      { label: "Susan Thomas", path: "/team/susan-thomas/" },
      { label: "Aaron Martinez", path: "/team/aaron-martinez/" },
      { label: "Stephen F. Hardy", path: "/team/stephen-hardy/" },
    ],
  },
  {
    id: "cases",
    label: "Cases",
    links: [
      { label: "All Cases & Results", path: "/cases/" },
      { label: "Car vs Motorcycle", path: "/practice-area/car-vs-motorcycle-accidents/" },
      { label: "Motorcycle vs. Animal", path: "/practice-area/animal-vs-motorcycle-accident/" },
      { label: "Hit-and-Run Accident", path: "/practice-area/motorcycle-hit-and-run-accident/" },
      { label: "Motorcyclists’ No-Fault Claims", path: "/practice-area/michigan-motorcyclists-no-fault-claims/" },
      { label: "Truck vs Motorcycle Accidents", path: "/practice-area/truck-vs-motorcycle-accident/" },
      { label: "Wrongful Death Motorcycle Accident", path: "/practice-area/wrongful-death-motorcycle-accident/" },
      { label: "Motorcycle vs. Motorcycle Accident", path: "/practice-area/motorcycle-vs-motorcycle-accident/" },
      { label: "Road Defect Motorcycle Crash", path: "/practice-area/road-defect-motorcycle-crash-accident/" },
    ],
  },
  {
    id: "locations",
    label: "Locations",
    links: [
      { label: "All locations", path: "/locations/" },
      { label: "Ann Arbor", path: "/locations/ann-arbor-motorcycle-accident-lawyer/" },
      { label: "Detroit", path: "/locations/detroit-motorcycle-accident-lawyer/" },
      { label: "Flint", path: "/locations/flint-motorcycle-accident-lawyer/" },
      { label: "Grand Rapids", path: "/locations/grand-rapids-motorcycle-accident-lawyer/" },
      { label: "Mount Clemens", path: "/locations/mount-clemens-motorcycle-accident-lawyer/" },
      { label: "Southfield", path: "/locations/southfield-motorcycle-accident-lawyer/" },
    ],
  },
  {
    id: "resources",
    label: "Resources",
    links: [
      { label: "Settlement Calculator", path: "/motorcycle-accident-settlement-calculator/" },
      { label: "Blog", path: "/blog/" },
      { label: "Videos", path: "/category/mc-legal-minute/" },
      { label: "eBooks", path: "/ebooks-for-michigan-motorcyclists/" },
      { label: "Phone App", path: "/best-motorcycle-phone-app/" },
      { label: "Accident Compensation Guide", path: "/michigan-motorcycle-accident-compensation-guide/" },
      { label: "Law & Insurance Guide", path: "/michigan-motorcycle-law-insurance-guide/" },
      { label: "Common questions", path: "/#answers" },
    ],
  },
  {
    id: "events",
    label: "Events",
    links: [
      { label: "All events", path: "/events/" },
      { label: "ASM Guide and Classes", path: "/accident-scene-management-training-michigan/" },
      { label: "National Motorcycle Day", path: "/national-motorcycle-day-event/" },
      { label: "The D-Frost Run Motorcycle Event", path: "/d-frost-run-motorcycle-ride/" },
    ],
  },
];

export const trustLinks: NavLink[] = [
  { label: "About the firm", path: "/about-us/" },
  { label: "Client testimonials", path: "/law-firm-testimonials/" },
  { label: "A motorcycle injury verdict story", path: "/27-million-motorcycle-injury-verdict/" },
];

export const helpTopics: HelpTopic[] = [
  {
    title: "Medical bills after a crash",
    summary: "Questions about no-fault benefits and available coverage.",
    path: "/practice-area/michigan-motorcyclists-no-fault-claims/",
  },
  {
    title: "A crash involving a car",
    summary: "Help with driver fault, evidence and insurance questions.",
    path: "/practice-area/car-vs-motorcycle-accidents/",
  },
  {
    title: "A driver left the scene",
    summary: "Questions about an unknown driver and possible coverage.",
    path: "/practice-area/motorcycle-hit-and-run-accident/",
  },
  {
    title: "A fatal motorcycle crash",
    summary: "Information for families after a rider’s death.",
    path: "/practice-area/wrongful-death-motorcycle-accident/",
  },
];

export const contactFacts: ContactFact[] = [
  {
    term: "How to reach the firm",
    detail: "Call 877-BIKER-LAW, or send the free case review form on this page.",
  },
  {
    term: "What the form asks for",
    detail: "Your first and last name, phone number and email. A ZIP code and crash details are optional.",
  },
  {
    term: "Cases the firm reviews",
    detail: "Michigan motorcycle injury and death cases.",
  },
  {
    term: "Cost of the review",
    detail: "The case review is free.",
  },
];

export const answers: Answer[] = [
  {
    id: "first-steps",
    question: "What should I do after a motorcycle crash?",
    answer: "Put safety and medical care first. When it is safe, take photos, save witness and insurance details, and follow police reporting instructions. Keep your damaged gear, medical records and crash papers together.",
    link: { label: "First steps after a crash", path: "/rider-resources/michigan-motorcycle-crash-first-24-hours/" },
  },
  {
    id: "medical-bills",
    question: "Who pays my medical bills after a motorcycle crash?",
    answer: "No-fault benefits may be available when a car or truck was involved, but coverage and eligibility depend on the facts. Motorcycle-only crashes follow different rules. The firm can review your crash and insurance information.",
    link: { label: "Medical bills and no-fault claims", path: "/practice-area/michigan-motorcyclists-no-fault-claims/" },
  },
  {
    id: "hit-and-run",
    question: "What if the driver left the scene?",
    answer: "You may still have options if the driver cannot be identified. Uninsured motorist coverage may apply, depending on your policy and the crash. Save the police report number, witness details and any information about the vehicle.",
    link: { label: "Help after a hit-and-run", path: "/practice-area/motorcycle-hit-and-run-accident/" },
  },
];

export const upcomingEvents: CommunityEvent[] = [
  {
    id: "flint-ice-race-2026",
    title: "Flint Motorcycle Club Indoor Motorcycle Ice Race",
    weekday: "Sat",
    dateLabel: "Saturday, December 5, 2026",
    monthShort: "Dec",
    day: "5",
    year: "2026",
    isoStart: "2026-12-05T19:00:00-05:00",
    timeLabel: "Racing at 7 pm Eastern",
    venue: "Dort Financial Center",
    location: "Flint, Michigan",
    path: "/event/flint-motorcycle-club-indoor-motorcycle-ice-race-2026/",
  },
];

export const footerGroups: NavGroup[] = [
  {
    id: "footer-firm",
    label: "The firm",
    links: [
      { label: "About the firm", path: "/about-us/" },
      { label: BRAND_NAME, path: "/team/jason-a-waechter/" },
      { label: "Client testimonials", path: "/law-firm-testimonials/" },
    ],
  },
  {
    id: "footer-help",
    label: "Get help",
    links: [
      { label: "Free case review", path: "/#case-review" },
      { label: "Common questions", path: "/#answers" },
      { label: "Events", path: "/events/" },
    ],
  },
];

export const resultsDisclaimer = "Past results do not guarantee future outcomes. Every case depends on its own facts.";

